import {
	type CSSProperties,
	type ComponentProps,
	Fragment,
	type PropsWithChildren,
	type ReactNode,
	useRef,
} from 'react';
import { Box, BoxProps } from 'reflexbox';
import styled, { css, keyframes } from 'styled-components';

import { tokens } from '../theme/tokens';

function Skeleton<T extends object, S>(element: T, Skeleton: S): T & { Skeleton: S } {
	return Object.assign(element, { Skeleton });
}

Skeleton.Repeat = function SkeletonRepeat(props: PropsWithChildren<{ count: number }>) {
	return (
		<>
			{[...new Array(props.count)].map((_, i) => (
				<Fragment key={i}>{props.children}</Fragment>
			))}
		</>
	);
};

const animate = keyframes`
	from { background-position: 0px 0px; }
	to { background-position: 400px 0px; }
`;

const skeletonLinearGradient = (foreground: string, background: string) => {
	return `
		linear-gradient(
			-45deg,
			${foreground} 0%,
			${background} 10%,
			${background} 40%,
			${foreground} 50%,
			${background} 60%,
			${background} 90%,
			${foreground} 100%
		)
	`;
};

type SkeletonTint = 'purple' | 'subtle';

const tintGradients: Record<SkeletonTint, string> = {
	purple: skeletonLinearGradient('#9906b733', '#4840be33'),
	// A barely visible black shimmer for use on colored/tinted surfaces.
	subtle: skeletonLinearGradient('rgba(0, 0, 0, 0.07)', 'rgba(0, 0, 0, 0.03)'),
};

const BaseBox = styled(Box)<{ animated?: boolean; tint?: SkeletonTint }>`
	animation: ${animate} 1s linear infinite;
	border-radius: 2px;

	${(props) =>
		props.animated === false &&
		css`
			background: ${props.color ?? props.theme.colors.lightBlueLight};
			animation: none;
		`}

	${(props) =>
		props.tint
			? css`
					background: ${tintGradients[props.tint]};
				`
			: css`
					background: ${skeletonLinearGradient(
						props.theme.colors.lightBlueLight,
						tokens.border.subtle,
					)};
				`}

	background-attachment: fixed;
	background-size: 400px 400px;
	background-repeat: repeat;
`;

Skeleton.Box = function Box(
	props: PropsWithChildren<
		BoxProps & { animated?: boolean; style?: CSSProperties; tint?: SkeletonTint }
	>,
) {
	return (
		<BaseBox
			ref={(ref?: HTMLDivElement) =>
				ref?.getAnimations?.().forEach((animation) => (animation.startTime = 0))
			}
			{...props}
		/>
	);
};

const CustomWidthSkeletonBox = styled(Skeleton.Box).attrs((props: { $width: number }) => ({
	style: { width: `${props.$width}px` },
}))``;

Skeleton.DynamicBox = function DynamicSkeletonBox(
	props: ComponentProps<typeof CustomWidthSkeletonBox> & { width?: number; maxWidth?: number },
) {
	const width = useRef(Math.random() * ((props.maxWidth ?? 200) - 100) + 100).current;

	return <CustomWidthSkeletonBox {...props} $width={props.width ?? width} />;
};

const EmptyContainer = styled(Box)`
	position: relative;

	${BaseBox} {
		background: ${(props) => props.theme.colors.lightBlueLight};
		animation: none;
	}
`;

Skeleton.Empty = function Empty(
	props: PropsWithChildren<BoxProps> & { contents: ReactNode; wide?: boolean },
) {
	return (
		<EmptyContainer {...props}>
			<Box opacity={0.5}>{props.children}</Box>
			<Box
				style={{
					position: 'absolute',
					left: '50%',
					top: '50%',
					transform: 'translate(-50%, -50%)',
					width: props.wide ? '100%' : undefined,
				}}
			>
				{props.contents}
			</Box>
		</EmptyContainer>
	);
};

export { Skeleton };
